<template>
  <CloseHeader :title="`${t('label_edit_interpretation')}`" />

  <div class="app-container modify-con">
    <div class="modify-head">
      <p>{{ t('label_interpretation_model') }}</p>
      <div class="model-info">
        {{ t(`label_${form.callType.toLowerCase()}`) }}
        ({{ formatNumberWithComma(form.limitCount) }}{{ t('label_person_count') }})
      </div>
    </div>

    <SettingChatDefault :form="form" />
    <AppDivider />

    <template v-if="hasVisibleOptions">
      <SettingChatOption :form="form" />
    </template>

    <!-- 
    <SettingChatAdvanced
      v-model:is-active-advanced-settings="isActiveAdvancedSettings"
      v-model:advanced-options="advancedOptions"
      v-model:gauge-options="gaugeOptions"
    />
    <SettingChatKeyword :form="form" /> 
    <SettingChatImage />
    -->

    <div class="btn-area">
      <button type="button" class="primary-btn l-btn" @click="updateChat">
        <span>{{ t('button_edit') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { editChatRoom, fetchQrCode } from '@/api/apiService';
import SettingChatDefault from '@/components/domains/mobile/chat/SettingChatDefault.vue';
import SettingChatOption from '@/components/domains/mobile/chat/SettingChatOption.vue';
import AppDivider from '@/components/domains/mobile/common/AppDivider.vue';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import { setIsBack } from '@/router';
import { useChatStore } from '@/stores/chat';
import { usePopupStore } from '@/stores/popup';
import { ChatItem } from '@/types/components';
import { formatNumberWithComma } from '@/utils/stringUtils.js';
import { CHAT_VISIBLE_OPTION_PRESETS } from '@/utils/constants/chat';
import { validateDescription, validateName } from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    code: string;
  }>(),
  {}
);

const { t } = useI18n();
const router = useRouter();
const popupStore = usePopupStore();

const { getChatInfo } = useChatStore();
// const { advancedOptions, isActiveAdvancedSettings, gaugeOptions } = useCreateOrUpdateFairConView();

const form = ref<ChatItem>({});
const entranceLogoImage = ref<File | null>(null);
const entranceLogoImageUrl = ref<string | null>(null);
const callLogoImage = ref<File | null>(null);
const callLogoImageUrl = ref<string | null>(null);

const hasVisibleOptions = computed<boolean>(() => {
  return CHAT_VISIBLE_OPTION_PRESETS[form.value.callType].length > 0;
});

// const convertAdvancedOptionsFromPercent = (options) => {
//   if (Object.keys(options).length > 0) isActiveAdvancedSettings.value = true;

//   const { TSS = 0, VAD = 0, SIL = 0 } = options;
//   return {
//     TSS: TSS * 100,
//     VAD: VAD * 20,
//     SIL: SIL * 20,
//   };
// };

// const convertAdvancedOptionsToPercent = () => {
//   if (!isActiveAdvancedSettings.value) return null;
//   return {
//     TSS: advancedOptions.value.TSS / 100,
//     VAD: advancedOptions.value.VAD / 20,
//     SIL: advancedOptions.value.SIL / 20,
//   };
// };

const fetchChatInfo = async (code: string) => {
  try {
    const { data } = await fetchQrCode(code);

    if (!data) {
      form.value = {};
    }

    form.value = data;
  } catch (error) {
    console.error('통역방 조회 실패: ', error);
  }
};

const validateFormData = () => {
  const errorMessage = validateName(form.value.title) || validateDescription(form.value.description);

  if (errorMessage) {
    popupStore.openToast(t(errorMessage));
    return false;
  }

  return true;
};

const updateChat = async () => {
  if (!validateFormData()) return null;

  const { callType = '', title = '', description = '', options = {} } = form.value;

  const body = {
    plan: form.value.planId,
    callType,
    title,
    description,
    keywords: [],
    options,
    partnerGroupId: null,
    advancedOptions: {},
    removeEntranceLogoImage: !entranceLogoImageUrl.value,
    removeCallLogoImage: !callLogoImageUrl.value,
  };

  const formData = new FormData();
  formData.append('body', JSON.stringify(body));

  if (entranceLogoImage.value) formData.append('entranceLogoImage', entranceLogoImage.value);
  if (callLogoImage.value) formData.append('callLogoImage', callLogoImage.value);

  const { success } = await editChatRoom(props.code, formData);

  if (!success) {
    popupStore.openToast(t('alert_try_again'));
    return;
  }

  popupStore.openToast(t('alert_update_success'));

  setIsBack(true);
  router.back();
};

onBeforeMount(async () => {
  form.value = getChatInfo();

  if (props.code !== form.value?.code) {
    await fetchChatInfo(props.code);
  }

  entranceLogoImageUrl.value = form.value.entranceLogoImageUrl;
  callLogoImageUrl.value = form.value.callLogoImageUrl;
  // advancedOptions.value = convertAdvancedOptionsFromPercent(form.value.advancedOptions);
});
</script>

<style scoped lang="scss">
.app-container {
  padding-bottom: 4.625rem;
}
.modify-con {
  .modify-head {
    text-align: center;
    padding: 1rem 0 1.5rem;
    p {
      font-size: $font-size-psmall;
    }
    .model-info {
      margin-top: $size-xs;
      display: inline-block;
      background: $gray10;
      padding: 0;
      font-size: $font-size-psmall;
      padding: 0.375rem 0.75rem;
      border-radius: 100px;
    }
  }
  .setting-group {
    max-height: 26px;
    overflow: hidden;
    transition: all 0.4s ease;
    &.open {
      max-height: 700px;
      .tit-wrap {
        .fold-btn {
          svg {
            transform: rotate(0);
          }
        }
      }
    }
    .tit-wrap {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .tit {
        font-size: $font-size-l;
      }
      .fold-btn {
        svg {
          transform: rotate(180deg);
        }
      }
    }
    .inr {
      padding-top: 1.5rem;
      p {
        font-size: $font-size-psmall;
        padding-right: $size-s;
      }
    }
  }
  .btn-area {
    margin-top: 2rem;
  }
}

.fold-btn {
  padding: 0;
  svg {
    width: 1.5rem;
    height: 1.5rem;
    transition: all 0.4s ease;
  }
}
</style>
